import { size } from "lodash";
import { Box } from "@mui/material";
import React, { useState } from "react";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
// component
import Button from "components/button/Button";
import { ReactComponent as DeleteIcon } from "assets/icons/Delete.svg";
import { ReactComponent as EditClass } from "assets/icons/EditClass.svg";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// direct import
import "../../member.scss";
import { createTouchedShape, membershipPricing } from "../../helper";

const PricingTabsManager = ({
  values,
  arrayHelpers,
  activeTab,
  setActiveTab,
  formik,
}) => {
  const [addingNew, setAddingNew] = useState(false);
  const [newTabTitle, setNewTabTitle] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);

  const handleAddNew = () => {
    if (!newTabTitle.trim()) return;

    const exists = values?.membershipPricing?.some(
      (item) => item?.title?.toLowerCase() === newTabTitle.toLowerCase()
    );
    if (exists) return alert("Price already exists");

    const maxOrder = Math.max(
      0,
      ...values.membershipPricing.map((item) => item.order || 0)
    );

    arrayHelpers.push({
      ...membershipPricing,
      title: newTabTitle,
      order: maxOrder + 1, // next order number
    });

    setNewTabTitle("");
    setAddingNew(false);
    setActiveTab(values.membershipPricing?.length);
  };

  const handleEdit = (index, sortedArray) => {
    arrayHelpers.replace(index, {
      ...sortedArray[index],
      title: editedTitle,
    });
    setEditingIndex(null);
    setEditedTitle("");
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (editingIndex !== null) return;

    // Sort before rearranging
    const sorted = values.membershipPricing
      .slice()
      .sort((a, b) => a.order - b.order);

    const { source, destination } = result;
    const [moved] = sorted.splice(source.index, 1);
    sorted.splice(destination.index, 0, moved);

    // Update order numbers
    const reordered = sorted.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    reordered.forEach((item, idx) => arrayHelpers.replace(idx, item));

    setActiveTab(destination.index);
  };

  // Always render sorted
  const sortedTabs = values?.membershipPricing
    ?.slice()
    .sort((a, b) => a.order - b.order);

  return (
    <Box className="top head_wrapper_top">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tabs" direction="vertical">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {sortedTabs?.map((item, index) => (
                <Draggable
                  key={`tab-${index}`}
                  draggableId={`tab-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`${editingIndex === index ? "editing" : ""} ${
                        activeTab === index
                          ? "cost__option active"
                          : "cost__option"
                      }`}
                    >
                      <Box
                        className="cost__option__value"
                        onClick={() => setActiveTab(index)}
                      >
                        {editingIndex !== index && (
                          <DragIndicatorIcon className="custom-drag-icon" />
                        )}
                        {editingIndex === index ? (
                          <input
                            type="text"
                            className="form-control"
                            maxLength={150}
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                          />
                        ) : (
                          item.title
                        )}
                      </Box>

                      <Box className="edit__cost">
                        {editingIndex === index && (
                          <>
                            <Button
                              variant="primary-outline"
                              size="sm"
                              onClick={() => setEditingIndex(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(index, sortedTabs)}
                            >
                              Save
                            </Button>
                          </>
                        )}
                      </Box>
                      {editingIndex !== index && (
                        <>
                          <Box
                            className="delete__cost"
                            onClick={() => {
                              setEditingIndex(index);
                              setEditedTitle(item.title);
                            }}
                          >
                            <EditClass />
                          </Box>
                          {size(sortedTabs) > 1 && (
                            <Box
                              className="delete__cost"
                              onClick={() => {
                                arrayHelpers.remove(index);
                                if (activeTab === index) {
                                  setActiveTab(0);
                                } else if (activeTab > index) {
                                  setActiveTab((prev) => prev - 1);
                                }
                              }}
                            >
                              <DeleteIcon />
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Box className="add_new_price">
        {addingNew ? (
          <>
            <Box>
              <input
                type="text"
                className="form-control"
                placeholder="+ Add New"
                maxLength={150}
                value={newTabTitle}
                onChange={(e) => setNewTabTitle(e.target.value)}
              />
            </Box>
            <Box className="addNewPrice_actions">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddNew}
                style={{ minWidth: "80px" }}
                type="button"
              >
                Ok
              </Button>
              <Button
                variant="primary-outline"
                size="sm"
                onClick={() => {
                  setAddingNew(false);
                  setNewTabTitle("");
                }}
                style={{ minWidth: "80px" }}
              >
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <span
            onClick={async () => {
              const errors = await formik.validateForm();

              const hasErrorsInMembershipPricing =
                errors.membershipPricing?.[
                  values?.membershipPricing?.length - 1
                ];

              if (!hasErrorsInMembershipPricing) {
                setAddingNew(true);
              } else {
                const touched = createTouchedShape(formik.values);
                formik.setTouched(touched, true);
              }
            }}
            className="add"
          >
            + Add New
          </span>
        )}
      </Box>
    </Box>
  );
};

export default PricingTabsManager;
